/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — COMBAT RESOLUTION
 * ===========================================================================
 *
 * Publishes `world.combat`:
 *
 *   damage(id, amount, srcId, opts)   direct damage; opts {knockback, launch,
 *                                     stun, dirX, dirZ, guardBreak, crit, ...}
 *   strike(desc)                      resolve ONE swept-arc hitbox against the
 *                                     world; returns the number of bodies hit
 *   beginSwing(id, move, opts)        an entity started a move; from now on its
 *                                     clip's hitboxOn/hitboxOff markers open
 *                                     and close real hitboxes
 *   endSwing(id)                      the move ended
 *   pulse(id, move, spec)             fire one arc immediately (musou grinder)
 *   nextToken()                       a fresh hit token
 *   hitboxes                          live array of open windows
 *   activeArc                         the newest open window, or null
 *   moveset                           the whole MOVESETS table
 *   lastHit / lastSweep               diagnostics the capture harness reads
 *   comboCount                        the player's live hit chain
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE THAT MATTERS
 * ---------------------------------------------------------------------------
 * A single sweep hits EVERY body inside its arc on the SAME frame. The window
 * opens, the whole angular span is tested at once, and every candidate that
 * passes takes the hit in that step — 6 to 15 bodies leaving the ground
 * together is not an edge case, it is the genre. Hit tokens
 * (`entities.hitToken`) make sure the same body is never hit twice by one
 * blade pass while the window stays open for enemies who walk into it. A
 * token is issued PER WINDOW, so a clip that authors two passes (C4's spin
 * opens a second, launching window) lands both — which is what DW does.
 *
 * ---------------------------------------------------------------------------
 * WHO MOVES A STAGGERED BODY
 * ---------------------------------------------------------------------------
 * `src/ai/*` owns every non-player body: it listens for the `'hit'` bus event,
 * puts the unit into FLINCH / LAUNCHED / DOWN / GETUP and integrates it
 * (`src/ai/steering.js`). Combat therefore does NOT move enemies. What it does
 * own is the IMPULSE: the moveset declares knockback in METRES and launch
 * height in METRES, and `applyImpulse()` converts those to the velocity the
 * AI's integrator will actually decay, so `knock: 3.4` really is 3.4 m of
 * travel. Everything else — the state machine, the reaction clip, separation,
 * the ground clamp — stays with the AI.
 *
 * If `world.ai` is ever absent (a bare harness, a unit test), combat falls
 * back to driving those bodies itself: `world.combat.ownsReactions` flips to
 * true and `stepReactions()` integrates them. That path is a safety net, not
 * the shipping one.
 * ===========================================================================
 */
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { ST, FLAG, KIND } from '../core/entities.js';
import { ACT } from '../ai/store.js';
import { WALLS, pushOut } from '../world/walls.js';

const _wk = { x: 0, z: 0, nx: 0, nz: 0 };
import { wrapPi, clamp } from '../core/mathx.js';
import { MOVESETS, HITSTOP, POISE_BY_KIND, REACT, F, PRESENTATION, validateMovesets } from './moveset.js';

const CAP = 2600;
const DEG = Math.PI / 180;
// A primary contact gets a ~71 ms state pulse; supporting bodies in a crowd
// sweep keep the same cue at lower amplitude so the first impact stays legible.
const HIT_FLASH_DECAY = 14.0;
const HIT_FLASH_PRIMARY_BODIES = 3;
const HIT_FLASH_SUPPORT_PEAK = 0.62;
const GRAV = 22;            // matches CFG.combat.launchGravity and steering.js
/* Decay constants the AI's integrator applies, so `knock` in metres survives
 * the trip: travel = v0 / k. See src/ai/steering.js. */
const K_BALLISTIC = 4.2, K_DEATH = 6.5, K_FLINCH = 9.0;

/* ------------------------------------------------------- module scratch ---
 * Nothing in fixedUpdate allocates. Everything below is built once. */

const _q = new Int32Array(1024);           // spatial query scratch
const _lastIds = new Int32Array(64);       // ids hit by the most recent sweep

/* Option bags handed to world.vfx / world.audio. Reused, never retained by
 * the callee — a single sweep can land 30 hits and 30 fresh objects a frame is
 * exactly the GC saw-tooth CONTRACTS.md section 6 forbids. */
const _vopt = { power: 1, scale: 1, kind: '', radius: 1, life: 0, thick: 0.1, alpha: 1,
  roll: 0, dirX: 0, dirY: 1, dirZ: 0, flash: true, hits: 0 };
const _aopt = { x: 0, y: 0, z: 0, gain: 1 };
/** opts handed to world.combat.damage / the 'hit' bus event. Read synchronously. */
const _hopt = { knockback: 0, launch: 0, stun: 0, guardBreak: false, dirX: 0, dirZ: 0,
  sweepIndex: 0, importance: 0, crit: false,
  // Combat routes the real contact through impact.hit() after damage accepts
  // the body. Disarm impact's legacy bare-hit fallback on this bus event or
  // the first body of a sweep gets a second spark/stop before `routed` flips.
  quiet: true };
/** opts handed to world.impact.hit(). Reused, read synchronously. */
const _iopt = { index: 0, noStop: false };
/** Sixth argument to ui.damage(); the UI reads this synchronously. */
const _dui = { index: 0, importance: 0, crit: false, player: false, officer: false, opts: null };
/** Entity states that mean "this body actually took the blow". An officer who
 *  parried or shrugged it off through super-armour is in none of them, and must
 *  NOT be shoved by the moveset's impulse. */
/**
 * Who shrugs off a blow's impulse. See the call site in applyHit() for why
 * this names the resisters rather than the yielders.
 */
const RESIST_FLAGS = FLAG.SUPERARMOR | FLAG.INVULN;
/** What a blockable blow is worth against a raised guard. See applyHit(). */
const GUARD_SOAK = 0.45;
const RESISTS = (e, id) => e.state[id] === ST.BLOCK || e.has(id, RESIST_FLAGS);
const _snd = (x, y, z, g) => { _aopt.x = x; _aopt.y = y; _aopt.z = z; _aopt.gain = g; return _aopt; };

/** Combat-owned reaction motion (see header). */
const kvx = new Float32Array(CAP), kvy = new Float32Array(CAP), kvz = new Float32Array(CAP);
const rpx = new Float32Array(CAP), rpy = new Float32Array(CAP), rpz = new Float32Array(CAP);
const rdrag = new Float32Array(CAP);
const rtime = new Float32Array(CAP);       // seconds left in the current reaction
const rown = new Uint8Array(CAP);          // 1 = combat drives this body
const ruid = new Int32Array(CAP);          // recycling guard
const deadT = new Float32Array(CAP);       // seconds since death

/** Swing contexts: an entity mid-move whose clip markers drive hitboxes. */
const SWINGS = 12;
const swingOf = new Int32Array(CAP).fill(-1);
const swings = [];
for (let i = 0; i < SWINGS; i++) {
  swings.push({ used: false, id: -1, move: null, token: 0, team: -1, yaw: 0, scale: 1, hits: 0, wins: 0 });
}

/** Hitbox windows. Pooled; `open` says whether one is live. */
const WINDOWS = 16;
const wins = [];
for (let i = 0; i < WINDOWS; i++) {
  wins.push({
    open: false, id: -1, slot: -1, token: 0, team: -1, srcId: -1, first: true,
    x: 0, y: 0, z: 0, yaw: 0, half: 90, bias: 0, r0: 0.3, r1: 2.8, y0: 0, y1: 2,
    damage: 0, poise: 0, knock: 0, drag: 4.2, lift: 0, launch: false, radial: 0.5,
    stun: 0, gb: false, hitstop: 'light', shake: 0.3, kick: 0.3, cap: 28,
    left: 0, hits: 0, vfx: 'slash', sfx: 'swing', presentation: PRESENTATION.COMBAT_VFX,
    sweepA: 0, sweepB: 0,
  });
}

/** One reusable descriptor so `strike()` never allocates on the hot path. */
const _d = {
  srcId: -1, team: -1, token: 0, x: 0, y: 0, z: 0, yaw: 0,
  half: 90, bias: 0, r0: 0.3, r1: 2.8, y0: 0, y1: 2,
  damage: 30, poise: 12, knock: 1.8, drag: 4.2, lift: 0, launch: false, radial: 0.55,
  stun: 0, gb: false, hitstop: 'light', shake: 0.3, kick: 0.3, cap: 28,
  vfx: 'slash', sfx: 'swing', presentation: PRESENTATION.COMBAT_VFX, hitDead: false,
};

const clipInfoOf = (w) => (name) => {
  const c = w.anim?.clip?.(name);
  if (!c) return null;
  let on = -1, off = -1;
  for (const e of c.events) {
    if (e.type === 'hitboxOn' && on < 0) on = e.t;
    if (e.type === 'hitboxOff') off = e.t;
  }
  return { dur: c.dur, on, off };
};

/* ========================================================================= */

export function createCombat() {
  let W = null;
  let token = 1;
  let checked = false;

  const api = {
    hits: 0,
    kills: 0,
    comboCount: 0,
    /** Set on the first fixed step: true only when there is no `world.ai`. */
    ownsReactions: false,
    moveset: MOVESETS,
    hitboxes: wins,
    activeArc: null,
    lastHit: { id: -1, x: 0, y: 0, z: 0, amount: 0, srcId: -1, t: 0, killed: false },
    lastSweep: { n: 0, hits: 0, x: 0, z: 0, y: 0, yaw: 0, r1: 0, half: 0, move: '', frame: 0, ids: _lastIds },
    stats: { sweeps: 0, hits: 0, kills: 0, multiBest: 0, launched: 0,
      semanticVfx: 0, musouHitVfx: 0, finisherVfx: 0,
      /* Guard telemetry. Three counters rather than one, because "the soak did
       * not fire" has three different causes and a single number cannot tell
       * them apart:
       *   blockStateSeen  applyHit found e.state === ST.BLOCK
       *   blockActSeen    the AI's own store says ACT.BLOCK — the authoritative
       *                   intent, where e.state is only the animation channel
       *   soaked          a blockable blow actually had its damage reduced
       * If blockActSeen is healthy and blockStateSeen is ~0, the bug is that
       * combat is reading the wrong field. */
      blockStateSeen: 0, blockActSeen: 0, soaked: 0 },
    nextToken() { return ++token; },
  };

  /** Drop every battle-local combat handle without reallocating its pools. */
  function resetBattle(w) {
    token = 1;
    swingOf.fill(-1);
    rown.fill(0); ruid.fill(0); deadT.fill(0);
    kvx.fill(0); kvy.fill(0); kvz.fill(0);
    rpx.fill(0); rpy.fill(0); rpz.fill(0); rdrag.fill(0); rtime.fill(0);
    _lastIds.fill(-1);
    w.entities?.hitToken?.fill(0);

    for (let i = 0; i < SWINGS; i++) {
      const s = swings[i];
      s.used = false; s.id = -1; s.move = null; s.token = 0; s.team = -1;
      s.yaw = 0; s.scale = 1; s.hits = 0; s.wins = 0;
    }
    for (let i = 0; i < WINDOWS; i++) {
      const hit = wins[i];
      hit.open = false; hit.id = -1; hit.slot = -1; hit.token = 0;
      hit.team = -1; hit.srcId = -1; hit.first = true; hit.left = 0; hit.hits = 0;
    }

    api.hits = 0; api.kills = 0; api.comboCount = 0; api.activeArc = null;
    const L = api.lastHit;
    L.id = -1; L.x = 0; L.y = 0; L.z = 0; L.amount = 0; L.srcId = -1; L.t = 0; L.killed = false;
    const S = api.lastSweep;
    S.n = 0; S.hits = 0; S.x = 0; S.y = 0; S.z = 0; S.yaw = 0;
    S.r1 = 0; S.half = 0; S.move = ''; S.frame = 0;
    api.stats.sweeps = 0; api.stats.hits = 0; api.stats.kills = 0;
    api.stats.multiBest = 0; api.stats.launched = 0;
    api.stats.semanticVfx = 0; api.stats.musouHitVfx = 0; api.stats.finisherVfx = 0;
    return api;
  }

  /* ------------------------------------------------------------- damage -- */

  function poiseFor(e, id) {
    if (e.maxPoise[id] > 0) return e.maxPoise[id];
    const base = POISE_BY_KIND[e.kind[id]] ?? 24;
    e.maxPoise[id] = base; e.poise[id] = base;
    return base;
  }

  function hitFlashPeak(opts) {
    return (opts?.sweepIndex ?? 0) < HIT_FLASH_PRIMARY_BODIES ? 1 : HIT_FLASH_SUPPORT_PEAK;
  }

  /**
   * Apply damage. Kept signature-compatible with the stub every other system
   * already calls: `world.combat.damage(id, amount, srcId, opts)`.
   */
  function damage(w, id, amount, srcId = -1, opts = null) {
    const e = w.entities;
    if (id < 0 || id >= CAP || !e.alive[id] || e.state[id] === ST.DEAD) return false;
    if (e.has(id, FLAG.INVULN)) return false;

    let amt = Math.max(0, amount);
    const isPlayer = id === w.playerId;
    const playerCanDie = !isPlayer || (w.player?.canDie ? w.player.canDie() : w.player?.allowDeath !== false);

    /* PARRY (DEV_PLAN Step 4). A blow arriving in the opening frames of a
     * guard is read rather than absorbed: no chip damage at all, the attacker
     * is staggered, and the defender is paid bravery.
     *
     * It is tested BEFORE the ordinary guard branch and, unlike that branch,
     * it is NOT excluded by `guardBreak` — beating a guard break is precisely
     * what the read is worth. The facing test is the same one: a parry is a
     * blow met, so it cannot be earned with your back turned. */
    if (isPlayer && w.player && w.player.parryWindowOpen) {
      const dx = (opts && opts.dirX) || 0, dz = (opts && opts.dirZ) || 0;
      const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
      if (-(dx * fx + dz * fz) > 0.10) {
        w.player.onParry(srcId, amount);
        return true;
      }
    }

    // Guard: chip damage from the front, full damage from behind.
    if (isPlayer && w.player && w.player.guarding && !(opts && opts.guardBreak)) {
      const dx = (opts && opts.dirX) || 0, dz = (opts && opts.dirZ) || 0;
      const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
      // dir points the way the blow travels; a frontal blow comes at the face
      const facing = -(dx * fx + dz * fz);
      if (facing > 0.10) {
        amt *= 0.16;
        if (!playerCanDie) amt = Math.min(amt, Math.max(0, e.hp[id] - 1));
        e.hp[id] = Math.max(playerCanDie ? 0 : 1, e.hp[id] - amt);
        e.flash[id] = Math.max(e.flash[id], hitFlashPeak(opts));
        w.player.guardHp = Math.max(0, (w.player.guardHp ?? 100) - amount * 0.35);
        w.anim?.play?.(id, 'block_impact', { blend: 0.05 });
        w.impact?.hit?.('guard', e.x[id], e.y[id] + 1.2, e.z[id], -fx, 0.4, -fz, 0.5);
        w.audio?.play?.('guard', _snd(e.x[id], e.y[id] + 1.2, e.z[id], 1));
        w.bus.emit('guarded', id, srcId, amount);
        if (w.player.guardHp <= 0) breakGuard(w, id);
        return true;
      }
    }

    if (!playerCanDie) amt = Math.min(amt, Math.max(0, e.hp[id] - 1));
    e.hp[id] -= amt;
    e.flash[id] = Math.max(e.flash[id], hitFlashPeak(opts));
    const killed = e.hp[id] <= 0;

    api.hits++; api.stats.hits++;
    const L = api.lastHit;
    L.id = id; L.x = e.x[id]; L.y = e.y[id] + 1.05; L.z = e.z[id];
    L.amount = amt; L.srcId = srcId; L.t = w.time.sim; L.killed = killed;

    if (killed) {
      e.hp[id] = 0;
      e.state[id] = ST.DEAD; e.prevState[id] = ST.DEAD; e.stateT[id] = 0;
      e.fade[id] = 1; deadT[id] = 0;
      e.setFlag(id, FLAG.AGGRO, false);
      api.kills++; api.stats.kills++;
      e.stats.kills++;
      if (srcId === w.playerId && w.player) w.player.kills++;
    }
    const officer = e.kind[id] === KIND.OFFICER || e.kind[id] === KIND.GENERAL || e.kind[id] === KIND.ALLY_OFFICER;
    const explicitImportance = opts?.importance ?? 0;
    _dui.index = opts?.sweepIndex ?? 0;
    _dui.crit = !!opts?.crit;
    _dui.player = isPlayer;
    _dui.officer = officer;
    _dui.importance = Math.max(explicitImportance, (_dui.crit || killed) ? 3 : 0,
      isPlayer ? 3 : 0, officer ? 2 : 0);
    _dui.opts = opts;
    w.ui?.damage?.(e.x[id], e.y[id] + 1.5, e.z[id], Math.round(amt), killed ? 'kill' : 'hit', _dui);
    _dui.opts = null;
    // 'hit' BEFORE 'kill': the AI's hit handler is the one that carries the
    // direction into the death launch, and its kill handler short-circuits
    // once the body is already marked dead.
    w.bus.emit('hit', id, srcId, amt, opts);
    if (killed) w.bus.emit('kill', id, srcId);
    if (isPlayer) {
      w.player?.hurt?.(amt, (opts && opts.dirX) || 0, (opts && opts.dirZ) || 0,
        (opts && opts.knockback) || 0.5, (opts && opts.launch) || 0, killed);
    }
    return true;
  }

  function breakGuard(w, id) {
    const e = w.entities;
    if (w.player) { w.player.guarding = false; w.player.guardHp = 0; w.player.guardBreakT = REACT.guardBreak; }
    e.state[id] = ST.GUARD_BREAK; e.stateT[id] = 0;
    w.anim?.play?.(id, 'guard_break', { blend: 0.06 });
    w.impact?.hit?.('guardBreak', e.x[id], e.y[id] + 1.3, e.z[id], 0, 1, 0, 1.2);
  }

  /* ------------------------------------------------------ reaction motion */

  function claim(w, id) {
    const e = w.entities;
    rown[id] = 1; ruid[id] = e.uid[id];
    rpx[id] = e.x[id]; rpy[id] = e.y[id]; rpz[id] = e.z[id];
  }
  function releaseBody(w, id) {
    rown[id] = 0;
    const e = w.entities;
    kvx[id] = 0; kvy[id] = 0; kvz[id] = 0;
    e.vx[id] = 0; e.vz[id] = 0; e.vy[id] = 0;
  }

  /**
   * The SHIPPING path. `src/ai/*` has already put the body into FLINCH /
   * LAUNCHED / DOWN (its `'hit'` bus handler ran synchronously inside
   * `damage()` above) and will integrate it next tick. All combat does is
   * replace the AI's generic impulse with the one the MOVESET asked for, so
   * `knock: 3.4` really means 3.4 metres of travel and `lift: 1.15` really
   * means 1.15 m of air.
   *
   * The conversion needs the decay the AI will apply, because travel = v0/k:
   *   launched / dead -> ballistic, exp(-4.2 t)   (steering.integrate mode 1)
   *   dead + sliding  -> exp(-6.5 t)              (steering.deathSlide)
   *   flinching       -> damp(v, 0, 9)            (steering.integrate mode 0)
   */
  function applyImpulse(w, id, ux, uz, d, killed) {
    const e = w.entities;
    const mass = e.mass[id] > 0.05 ? e.mass[id] : 1;
    const air = killed || d.launch || d.lift > 0.5;
    const k = killed ? K_DEATH : air ? K_BALLISTIC : K_FLINCH;
    const v0 = (d.knock * k) / mass;
    e.vx[id] = ux * v0; e.vz[id] = uz * v0;
    if (d.lift > 0.01) e.vy[id] = Math.sqrt(2 * GRAV * (d.lift / mass));

    poiseFor(e, id);
    e.poise[id] -= d.poise;
    if (e.poise[id] <= 0) {
      e.poise[id] = e.maxPoise[id];
      e.stagger[id] = Math.max(e.stagger[id], 0.85);   // picks hit_heavy in the animator
    }

    // A stun move holds the body still and open. The AI runs its flinch off
    // `store.dur`; extend it there (guarded — see docs/REQUESTS.md for the
    // `world.ai.stagger(id, seconds)` this would rather call).
    if (d.stun > 0.01 && !killed) {
      e.vx[id] = 0; e.vz[id] = 0;
      e.stagger[id] = 1;
      const A = w.ai?.store;
      if (A && A.dur) { A.dur[id] = Math.max(A.dur[id] || 0, d.stun); A.actT[id] = 0; A.think[id] = d.stun + 0.05; }
      w.ai?.stagger?.(id, d.stun);
    }
  }

  /**
   * FALLBACK ONLY (no `world.ai`): put `id` into a reaction combat drives
   * itself. `ux/uz` is the unit direction the body travels, `dist` metres of
   * knockback, `lift` metres of launch height.
   */
  function react(w, id, ux, uz, dist, lift, drag, stun, killed, launch, srcId) {
    const e = w.entities;
    const mass = e.mass[id] > 0.05 ? e.mass[id] : 1;
    claim(w, id);
    rdrag[id] = drag > 0.2 ? drag : 4.2;
    // distance travelled by v0 * exp(-drag t) is v0 / drag
    const v0 = (dist * rdrag[id]) / mass;
    kvx[id] = ux * v0; kvz[id] = uz * v0;
    const g = -CFG.combat.launchGravity;              // +22
    kvy[id] = lift > 0.01 ? Math.sqrt(2 * g * lift / mass) : 0;

    if (killed) {
      e.stagger[id] = 1;
      rtime[id] = 6;
      if (kvy[id] > 3.2 || v0 > 9) w.anim?.play?.(id, 'death_launch', { blend: 0.05 });
      api.stats.launched++;
      return;
    }
    if (launch || kvy[id] > 1.2) {
      e.state[id] = ST.LAUNCH; e.stateT[id] = 0; e.stagger[id] = 1;
      rtime[id] = REACT.launchHold;
      e.setFlag(id, FLAG.JUGGLED, true);
      api.stats.launched++;
    } else if (stun > 0) {
      e.state[id] = ST.STUN; e.stateT[id] = 0; e.stagger[id] = 1;
      rtime[id] = stun;
    } else {
      const broke = e.poise[id] <= 0;
      e.state[id] = ST.HIT; e.stateT[id] = 0;
      e.stagger[id] = broke ? 0.95 : 0.2;
      rtime[id] = broke ? REACT.stagger : REACT.flinch;
      if (broke) e.poise[id] = e.maxPoise[id];
    }
    e.target[id] = srcId;
  }

  /** Integrate every body combat currently owns. Runs after AI, so it wins. */
  function stepReactions(w, dt) {
    const e = w.entities, ter = w.terrain;
    const g = CFG.combat.launchGravity;
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (!rown[id]) continue;
      if (ruid[id] !== e.uid[id]) { rown[id] = 0; continue; }
      const st = e.state[id];

      // integrate
      const dec = Math.exp(-rdrag[id] * dt);
      rpx[id] += kvx[id] * dt; rpz[id] += kvz[id] * dt;
      kvx[id] *= dec; kvz[id] *= dec;
      rpy[id] += kvy[id] * dt; kvy[id] += g * dt;

      const H = CFG.world.half - 6;
      if (rpx[id] > H) { rpx[id] = H; kvx[id] *= -0.35; }
      if (rpx[id] < -H) { rpx[id] = -H; kvx[id] *= -0.35; }
      if (rpz[id] > H) { rpz[id] = H; kvz[id] *= -0.35; }
      if (rpz[id] < -H) { rpz[id] = -H; kvz[id] *= -0.35; }
      // a man thrown into a wall stops at it and comes back off it the way
      // he comes off the world's edge: the part of his flight into the face
      // reflected at the same 0.35
      if (WALLS.n && pushOut(rpx[id], rpz[id], e.radius[id] > 0 ? e.radius[id] : 0.45, _wk)) {
        rpx[id] = _wk.x; rpz[id] = _wk.z;
        const vn = kvx[id] * _wk.nx + kvz[id] * _wk.nz;
        if (vn < 0) { kvx[id] -= 1.35 * vn * _wk.nx; kvz[id] -= 1.35 * vn * _wk.nz; }
      }

      const gh = ter ? ter.heightAt(rpx[id], rpz[id]) : 0;
      let landed = false;
      if (rpy[id] <= gh) {
        if (kvy[id] < -6 && st !== ST.DEAD) {
          // hard landing: one bounce, then flat on the back
          kvy[id] = -kvy[id] * 0.22;
          rpy[id] = gh;
          landed = true;
        } else {
          rpy[id] = gh; kvy[id] = 0;
          landed = true;
        }
      }

      e.x[id] = rpx[id]; e.y[id] = rpy[id]; e.z[id] = rpz[id];
      e.vx[id] = kvx[id]; e.vy[id] = kvy[id]; e.vz[id] = kvz[id];
      e.speed[id] = 0;                       // never blend into the run cycle

      if (st === ST.DEAD) {
        if (landed) { kvx[id] *= 0.72; kvz[id] *= 0.72; }
        if (landed && Math.abs(kvx[id]) < 0.15 && Math.abs(kvz[id]) < 0.15) { rown[id] = 0; }
        continue;
      }

      rtime[id] -= dt;
      const airborne = rpy[id] > gh + 0.06;

      switch (st) {
        case ST.LAUNCH:
          if (rtime[id] <= 0) { e.state[id] = airborne ? ST.AIRBORNE : ST.DOWN; e.stateT[id] = 0; rtime[id] = airborne ? 4 : REACT.down; }
          break;
        case ST.AIRBORNE:
          if (!airborne) {
            e.state[id] = ST.DOWN; e.stateT[id] = 0; rtime[id] = REACT.down;
            e.setFlag(id, FLAG.JUGGLED, false);
            _vopt.kind = 'land'; w.vfx?.dust?.(e.x[id], gh + 0.1, e.z[id], 0.8, _vopt);
            w.audio?.play?.('bodyfall', _snd(e.x[id], gh, e.z[id], 1));
          } else if (rtime[id] <= 0) { e.state[id] = ST.DOWN; e.stateT[id] = 0; rtime[id] = REACT.down; }
          break;
        case ST.DOWN:
          if (rtime[id] <= 0) { e.state[id] = ST.GETUP; e.stateT[id] = 0; rtime[id] = REACT.getup; }
          break;
        case ST.GETUP:
          if (rtime[id] <= 0) { e.state[id] = ST.IDLE; e.stateT[id] = 0; e.stagger[id] = 0; releaseBody(w, id); }
          break;
        case ST.HIT:
        case ST.STUN:
        case ST.GUARD_BREAK:
          if (rtime[id] <= 0) {
            if (airborne) { e.state[id] = ST.AIRBORNE; rtime[id] = 4; }
            else { e.state[id] = ST.IDLE; e.stateT[id] = 0; e.stagger[id] = 0; releaseBody(w, id); }
          }
          break;
        default:
          if (rtime[id] <= 0 && !airborne) { e.stagger[id] = 0; releaseBody(w, id); }
          break;
      }
    }
  }

  /* ------------------------------------------------------------- strike -- */

  /** Consume the move's semantic VFX exactly once for a connected sweep. */
  function emitSweepVfx(w, d, hits, power) {
    const flags = d.presentation ?? PRESENTATION.COMBAT_VFX;
    if (!(flags & PRESENTATION.COMBAT_VFX) || !d.vfx || d.vfx === 'none') return;
    const quiet = (flags & PRESENTATION.QUIET_IMPACT) !== 0;
    const finisher = (flags & PRESENTATION.MUSOU_FINISHER) !== 0;
    _vopt.kind = d.vfx;
    _vopt.power = quiet ? clamp(0.18 + hits * 0.035, 0.18, 0.48) : power;
    _vopt.scale = finisher ? 1.6 : quiet ? 0.72 : clamp(0.75 + hits * 0.025, 0.75, 1.25);
    _vopt.radius = d.r1; _vopt.life = finisher ? 0.7 : quiet ? 0.22 : 0.4;
    _vopt.dirX = Math.sin(d.yaw); _vopt.dirY = 0.2; _vopt.dirZ = Math.cos(d.yaw);
    _vopt.flash = finisher; _vopt.hits = hits;
    w.vfx?.emit?.(d.vfx, d.x, d.y + (finisher ? 1.1 : 0.75), d.z, _vopt);
    api.stats.semanticVfx++;
    if (quiet) api.stats.musouHitVfx++;
    if (finisher) api.stats.finisherVfx++;
  }

  /**
   * Resolve one swept arc. `d` may be the shared `_d` or any object with the
   * same fields; `d.y0/d.y1` are relative to `d.y` (the attacker's feet).
   * Returns the number of bodies hit.
   */
  function strike(w, d) {
    const e = w.entities;
    const reach = d.r1 + 1.4;
    const n = e.around(d.x, d.z, reach, _q, _q.length);
    const half = d.half * DEG;
    const full = d.half >= 179.5;
    const bias = (d.bias || 0) * DEG;
    const centre = d.yaw + bias;
    const ay0 = d.y + d.y0, ay1 = d.y + d.y1;
    const cap = d.cap > 0 ? d.cap : 28;
    const hitDead = !!d.hitDead;
    let hits = 0;

    for (let i = 0; i < n; i++) {
      const id = _q[i];
      if (id === d.srcId || !e.alive[id]) continue;
      if (e.hitToken[id] === d.token) continue;
      const dead = e.state[id] === ST.DEAD;
      if (dead && !hitDead) continue;
      if (d.team >= 0 && e.team[id] === d.team) continue;
      /* PERFECT DODGE (DEV_PLAN Step 4). An invulnerable body is normally
       * skipped here, before any geometry runs — which means i-frames make a
       * body invisible to the sweep and there is no way to know whether a blow
       * would have landed. The player is let through to the geometry while
       * their dodge window is open, and ONLY then; everyone else keeps the
       * original fast path, at the cost of one boolean.
       *
       * Letting the geometry decide is the point: a perfect dodge has to be a
       * hitbox that genuinely would have connected, never a guess about what
       * the attacker meant. */
      const invuln = e.has(id, FLAG.INVULN);
      if (invuln && !(id === w.playerId && w.player && w.player.dodgeWindowOpen)) continue;

      const dx = e.x[id] - d.x, dz = e.z[id] - d.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const rad = e.radius[id] > 0.02 ? e.radius[id] : 0.42;
      if (dist > d.r1 + rad) continue;
      if (dist < d.r0 - rad) continue;

      if (!full) {
        // A body of radius `rad` at `dist` subtends this much extra angle, so
        // a man standing on the edge of the arc is clipped, not missed.
        const slack = dist > 0.08 ? Math.atan2(rad, dist) : Math.PI;
        let da = wrapPi(Math.atan2(dx, dz) - centre);
        if (da < 0) da = -da;
        if (da > half + slack) continue;
      }

      const eh = (e.height[id] > 0.1 ? e.height[id] : 1.75) * (e.scale[id] || 1);
      const bot = e.y[id], top = bot + eh;
      if (top < ay0 || bot > ay1) continue;

      if (invuln) {
        // it would have landed; the dodge beat it
        e.hitToken[id] = d.token;        // one sweep reads once
        w.player.onPerfectDodge(d.srcId);
        continue;
      }
      if (!applyHit(w, id, d, dx, dz, dist, hits)) continue;
      if (hits < 64) _lastIds[hits] = id;
      hits++;
      if (hits >= cap) break;
    }

    // one sweep-level feedback burst, whatever the body count
    const S = api.lastSweep;
    S.n = hits; S.hits = hits; S.x = d.x; S.z = d.z; S.y = d.y; S.yaw = d.yaw;
    S.r1 = d.r1; S.half = d.half; S.frame = w.time.tick;
    api.stats.sweeps++;
    if (hits > api.stats.multiBest) api.stats.multiBest = hits;

    if (hits > 0) {
      const hs = HITSTOP[d.hitstop] ?? HITSTOP.light;
      if (hs > 0) w.engine.hitstop(hs * (hits > 3 ? 1.15 : 1));
      const power = clamp(0.4 + hits * 0.12, 0.4, 1.6);
      w.camrig?.shake?.(d.shake * (1 + Math.min(hits, 8) * 0.045), 26, 0.20);
      w.camrig?.kickback?.(d.kick, Math.sin(d.yaw), Math.cos(d.yaw));
      // one image punch per sweep, graded by what the sweep actually was.
      // A `hitstop:'none'` window is the musou grinder pulsing at 8 Hz; it asked
      // to be quiet, so it does not get to punch the image eight times a second.
      if (d.hitstop !== 'none') {
        w.pipeline?.impulse?.(
          d.hitstop === 'musou' ? 'musou' : d.launch ? 'launch' : d.hitstop === 'huge' ? 'crit'
            : d.hitstop === 'heavy' ? 'heavy' : 'light',
          clamp(0.5 + hits * 0.06, 0.5, 1.0));
      }
      w.audio?.play?.(d.sfx, _snd(d.x, d.y + 1.2, d.z, power));
      emitSweepVfx(w, d, hits, power);
    }
    return hits;
  }

  function applyHit(w, id, d, dx, dz, dist, index) {
    const e = w.entities;
    e.hitToken[id] = d.token;

    // travel direction: radial (away from the attacker) blended with facing
    let ux, uz;
    if (dist > 1e-3) { ux = dx / dist; uz = dz / dist; }
    else { ux = Math.sin(d.yaw); uz = Math.cos(d.yaw); }
    const fx = Math.sin(d.yaw), fz = Math.cos(d.yaw);
    const b = d.radial;
    ux = ux * b + fx * (1 - b); uz = uz * b + fz * (1 - b);
    const ul = Math.hypot(ux, uz) || 1; ux /= ul; uz /= ul;

    const hx = e.x[id], hy = e.y[id] + Math.min(1.35, Math.max(0.5, d.y1 * 0.55)), hz = e.z[id];

    _hopt.knockback = d.knock; _hopt.launch = d.lift; _hopt.stun = d.stun;
    _hopt.guardBreak = d.gb; _hopt.dirX = ux; _hopt.dirZ = uz;
    _hopt.sweepIndex = index; _hopt.importance = 0;
    _hopt.crit = (d.presentation & PRESENTATION.MUSOU_FINISHER) !== 0;

    /* A RAISED GUARD NOW COSTS THE ATTACKER SOMETHING.
     *
     * Until this line, ST.BLOCK gated the knockback impulse (see RESISTS) and
     * nothing else: a man behind his guard took full damage, so the guard was
     * an animation. `unblockable` had sat in the move schema since the first
     * moveset shipped with ZERO readers, which is why the two facts never met.
     *
     * The size of the rebalance was measured before the number was chosen, not
     * after. guard_census, over sixty seconds of live battle across three
     * weapons: 7.5%, 7.8% and 8.9% of LANDED hits arrive on a body already in
     * ST.BLOCK — 99 of 1,237. So GUARD_SOAK at 0.45 costs the player about
     * 4.4% of total damage output, which is a modest, legible change rather
     * than a rebalance of a combat that has been judged at its current numbers.
     *
     * (That census read 0 of 1,237 for two runs first, because it asked
     * whether the victim was blocking AFTER the blow landed. The state before
     * the step is the only one that answers the question.)
     *
     * An unblockable move ignores it entirely, which is what makes the kapak's
     * four finishers mean something and gives the player a reason to carry an
     * answer to a guard rather than simply out-damaging it. */
    const A_ = w.ai?.store;
    const byState = e.state[id] === ST.BLOCK;
    const byAct = !!A_ && A_.act[id] === ACT.BLOCK;
    if (byState) api.stats.blockStateSeen++;
    if (byAct) api.stats.blockActSeen++;
    const guarding = byState || byAct;
    const soaked = guarding && !d.unblockable;
    if (soaked) api.stats.soaked++;
    const dmg = soaked ? d.damage * GUARD_SOAK : d.damage;
    const ok = damage(w, id, dmg, d.srcId, _hopt);
    if (!ok) return false;
    const killed = e.state[id] === ST.DEAD;

    // The player's own reaction is raised inside damage() (src/game/player.js
    // owns his motion). Everything else is an AI body.
    if (id !== w.playerId) {
      if (api.ownsReactions) {
        poiseFor(e, id);
        e.poise[id] -= d.poise;
        react(w, id, ux, uz, d.knock, d.lift, d.drag, d.stun, killed, d.launch, d.srcId);
      } else if (!RESISTS(e, id)) {
        // Everything the blow reaches takes the moveset's impulse EXCEPT the
        // bodies that legitimately shrug it off — an officer parrying, or one
        // roaring under super-armour (src/ai/officer.js). This used to be the
        // other way round: a mask enumerating the states that YIELD (HIT,
        // LAUNCH, AIRBORNE, DOWN, GUARD_BREAK, DEAD, STUN). That is equivalent
        // today, because damage() emits 'hit' synchronously at :220 and the
        // AI's handler moves the body to ST.HIT/ST.LAUNCH inline before this
        // line reads e.state — verified by replay, 28 of 28 bodies in a ch1 arc
        // pass and leave the ground from RUN, IDLE, WINDUP, RECOVER and BLOCK.
        // But it is only correct while that reaction stays synchronous. Defer
        // it to the next fixed step and the old mask would silently stop
        // applying EVERY impulse in the game, with nothing failing loudly.
        // Naming who resists instead of who yields cannot break that way.
        applyImpulse(w, id, ux, uz, d, killed);
      }
    }

    // Feedback — every body hit gets its OWN contact point, routed through
    // world.impact.hit() (hitstop + shake + spark + sfx).
    //
    // The POWER, though, falls off with the body count. A C4 into a packed
    // ring lands 20-26 hits on one frame, and a spark of constant brightness
    // per body turns the frame into a white disc — total emitted light has to
    // stay roughly constant however many men are in the arc, which is what the
    // real games do. The first three contacts read at full strength and the
    // rest fill in behind them.
    const fall = index < 3 ? 1 : 3 / (index + 1);
    _iopt.index = index;
    _iopt.noStop = d.hitstop === 'none' || (d.presentation & PRESENTATION.MUSOU_FINISHER) !== 0;
    w.impact?.hit?.(killed ? 'kill' : d.stun > 0 ? 'blunt' : 'slash', hx, hy, hz, ux, 0.55, uz,
      clamp(0.35 + d.damage / 90, 0.35, 1.6) * fall, _iopt);
    return true;
  }

  /* ------------------------------------------------------------- swings -- */

  function findSwing() {
    for (let i = 0; i < SWINGS; i++) if (!swings[i].used) return i;
    return -1;
  }
  function findWindow() {
    for (let i = 0; i < WINDOWS; i++) if (!wins[i].open) return i;
    return -1;
  }

  /**
   * An entity has started `move`. Its clip's hitboxOn/hitboxOff markers now
   * open and close real hitboxes with this move's properties.
   */
  function beginSwing(w, id, move, opts) {
    endSwing(w, id);
    const s = findSwing();
    if (s < 0) return -1;
    const sw = swings[s];
    sw.used = true; sw.id = id; sw.move = move; sw.token = ++token;
    sw.team = (opts && opts.team != null) ? opts.team : w.entities.team[id];
    sw.scale = (opts && opts.scale) || 1;
    sw.yaw = w.entities.yaw[id];
    sw.hits = 0; sw.wins = 0;
    swingOf[id] = s;
    return sw.token;
  }

  function endSwing(w, id) {
    const s = swingOf[id];
    if (s < 0) return;
    closeWindows(id);
    swings[s].used = false; swings[s].id = -1; swings[s].move = null;
    swingOf[id] = -1;
  }

  function closeWindows(id) {
    for (let i = 0; i < WINDOWS; i++) if (wins[i].open && wins[i].id === id) wins[i].open = false;
    if (api.activeArc && api.activeArc.id === id) api.activeArc = null;
  }

  /**
   * Open a hitbox window for a swing. `marker` is the clip's hitboxOn event
   * (may be null for a move-declared window); `ov` is an optional override
   * block from `move.extra`.
   */
  function openWindow(w, sw, marker, ov) {
    const wi = findWindow();
    if (wi < 0) return null;
    const e = w.entities, id = sw.id, m = sw.move;
    const arc = (ov && ov.arc) || m.arc;
    const k = sw.scale;
    const W_ = wins[wi];
    // One token PER WINDOW, not per move: inside a single blade pass nobody is
    // ever hit twice, but a clip that authors two passes (C4's spin opens a
    // second, launching window) lands both, which is what DW does.
    W_.open = true; W_.id = id; W_.token = ++token; W_.team = sw.team; W_.srcId = id;
    W_.first = true;
    W_.half = arc.half; W_.bias = arc.bias || 0;
    W_.r0 = arc.r0 * k; W_.r1 = arc.r1 * k;
    W_.y0 = arc.y0; W_.y1 = arc.y1 * k;
    W_.damage = (ov && ov.damage != null ? ov.damage : m.damage);
    W_.poise = (ov && ov.poise != null ? ov.poise : m.poise);
    W_.knock = (ov && ov.knock != null ? ov.knock : m.knock);
    W_.drag = (ov && ov.drag != null ? ov.drag : m.drag);
    W_.lift = (ov && ov.lift != null ? ov.lift : m.lift);
    W_.launch = (ov && ov.launch != null ? ov.launch : m.launch) || W_.lift > 0.9;
    W_.radial = (ov && ov.radial != null ? ov.radial : m.radial);
    W_.stun = (ov && ov.stun != null ? ov.stun : m.stun);
    W_.gb = m.gb; W_.unblockable = !!m.unblockable;
    W_.hitstop = (ov && ov.hitstop) || m.hitstop;
    W_.shake = m.shake; W_.kick = m.kick; W_.cap = m.hitCap;
    W_.vfx = (ov && ov.vfx) || m.vfx; W_.sfx = m.sfx;
    W_.presentation = (ov && ov.presentation != null) ? ov.presentation : m.presentation;
    W_.sweepA = arc.sweep ? arc.sweep[0] : 0; W_.sweepB = arc.sweep ? arc.sweep[1] : 0;
    // A marker that launches or guard-breaks upgrades the move's own flags —
    // the animator authored the second window of C4 as the launcher.
    if (marker) {
      if (marker.launch) { W_.launch = true; if (W_.lift < 0.9) W_.lift = Math.max(W_.lift, 1.1); }
      if (marker.gb) W_.gb = true;
      if (marker.dmg) W_.damage = W_.damage * clamp(marker.dmg / 1.2, 0.7, 1.6);
    }
    W_.left = (ov && ov.dur ? ov.dur : m.active) + 2;
    W_.hits = 0;
    sw.wins++;
    api.activeArc = W_;
    resolveWindow(w, W_);
    return W_;
  }

  /** Test one open window against the world, from the attacker's live pose. */
  function resolveWindow(w, W_) {
    const e = w.entities, id = W_.id;
    if (!e.alive[id]) { W_.open = false; return 0; }
    _d.srcId = id; _d.team = W_.team; _d.token = W_.token;
    _d.x = e.x[id]; _d.y = e.y[id]; _d.z = e.z[id]; _d.yaw = e.yaw[id];
    _d.half = W_.half; _d.bias = W_.bias; _d.r0 = W_.r0; _d.r1 = W_.r1;
    _d.y0 = W_.y0; _d.y1 = W_.y1;
    _d.damage = W_.damage; _d.poise = W_.poise; _d.knock = W_.knock; _d.drag = W_.drag;
    _d.lift = W_.lift; _d.launch = W_.launch; _d.radial = W_.radial;
    _d.stun = W_.stun; _d.gb = W_.gb; _d.unblockable = W_.unblockable;
    _d.hitstop = W_.hitstop;
    _d.shake = W_.shake; _d.kick = W_.kick; _d.cap = W_.cap;
    _d.vfx = W_.vfx; _d.sfx = W_.sfx; _d.presentation = W_.presentation; _d.hitDead = false;
    const n = strike(w, _d);
    W_.hits += n;
    W_.first = false;
    const s = swingOf[id];
    if (s >= 0) swings[s].hits += n;
    if (n > 0) {
      api.lastSweep.move = swings[s] && swings[s].move ? swings[s].move.id : '';
      if (id === w.playerId && w.player) w.player.onHits?.(n);
    }
    return n;
  }

  /** Fire one arc immediately, outside the marker system (musou grinder). */
  function pulse(w, id, spec, tk) {
    const e = w.entities;
    if (!e.alive[id]) return 0;
    const arc = spec.arc;
    _d.srcId = id; _d.team = e.team[id]; _d.token = tk || ++token;
    _d.x = e.x[id]; _d.y = e.y[id]; _d.z = e.z[id]; _d.yaw = e.yaw[id];
    _d.half = arc.half; _d.bias = arc.bias || 0; _d.r0 = arc.r0; _d.r1 = arc.r1;
    _d.y0 = arc.y0; _d.y1 = arc.y1;
    _d.damage = spec.damage; _d.poise = spec.poise; _d.knock = spec.knock;
    _d.drag = spec.drag ?? 4.2; _d.lift = spec.lift ?? 0; _d.launch = !!spec.launch;
    _d.radial = spec.radial ?? 1; _d.stun = spec.stun ?? 0; _d.gb = !!spec.gb;
    _d.hitstop = spec.hitstop || 'none'; _d.shake = spec.shake ?? 0.15;
    _d.kick = spec.kick ?? 0; _d.cap = spec.hitCap ?? 40;
    _d.vfx = spec.vfx || 'slash'; _d.sfx = spec.sfx || 'swing';
    _d.presentation = spec.presentation ?? PRESENTATION.COMBAT_VFX; _d.hitDead = false;
    const n = strike(w, _d);
    if (n > 0 && id === w.playerId && w.player) w.player.onHits?.(n);
    return n;
  }

  /* ------------------------------------------------------------- events -- */

  function onAnimEvent(w, id, m) {
    const s = swingOf[id];
    if (s < 0) return;                       // not mid-move: ignore (crowd chatter)
    const sw = swings[s];
    const mv = sw.move;
    if (!mv) return;
    switch (m.type) {
      case 'hitboxOn':
        if (!mv.ownWindows) openWindow(w, sw, m, null);
        break;
      case 'hitboxOff':
        if (!mv.ownWindows) closeWindows(id);
        break;

      // Audio and VFX subscribe to animation markers directly. Combat handles
      // only hitbox ownership here; replaying swoosh/voice/vfx would duplicate
      // those presentation hooks.
      default: break;
    }
  }

  /* ======================================================= system object == */

  return {
    name: 'combat', order: ORDER.COMBAT,

    init(w) {
      W = w;
      for (let i = 0; i < CAP; i++) { swingOf[i] = -1; rown[i] = 0; }

      w.combat = {
        get hits() { return api.hits; },
        get kills() { return api.kills; },
        get comboCount() { return w.player ? w.player.chain : 0; },
        get activeArc() { return api.activeArc && api.activeArc.open ? api.activeArc : null; },
        get lastHit() { return api.lastHit; },
        get lastSweep() { return api.lastSweep; },
        hitboxes: wins,
        moveset: MOVESETS,
        stats: api.stats,
        get ownsReactions() { return api.ownsReactions; },
        set ownsReactions(v) { api.ownsReactions = !!v; },

        /**
         * Count a launch that THIS module did not drive. `react()` below is
         * the fallback for when there is no `world.ai`, so in a real battle it
         * never runs and `stats.launched` sat at 0 while bodies were 2.9 m off
         * the ground -- which is what produced a false "the launcher does not
         * launch" verdict that then had to be withdrawn. The AI calls this
         * from its own launch branch so the counter means the same thing
         * whichever system owns reactions.
         */
        noteLaunch: () => { api.stats.launched++; },

        damage: (id, amt, src, opts) => damage(w, id, amt, src, opts),
        strike: (d) => strike(w, d),
        beginSwing: (id, move, opts) => beginSwing(w, id, move, opts),
        endSwing: (id) => endSwing(w, id),
        closeWindows,
        pulse: (id, spec, tk) => pulse(w, id, spec, tk),
        nextToken: () => ++token,
        resetBattle: () => resetBattle(w),
        /** True while `id` has a live hitbox. */
        isActive(id) { for (let i = 0; i < WINDOWS; i++) if (wins[i].open && wins[i].id === id) return true; return false; },
        /** How many bodies are inside a hypothetical arc — used by the AI/HUD. */
        countIn(x, z, r, team) { return w.entities.countIn(x, z, r, team); },
        /** Hand a body back to whoever owns physics next. */
        disown(id) { rown[id] = 0; },
        owns(id) { return !!rown[id]; },
      };

      // The animation system initialises AFTER combat (ORDER.ANIM > ORDER.COMBAT),
      // so its markers are picked up off the bus rather than through anim.onEvent().
      w.bus.on('anim:event', (id, m) => onAnimEvent(w, id, m));
    },

    /**
     * Hit flash decays on REAL time, deliberately, and not in fixedUpdate.
     *
     * A hit sets e.flash to a primary/support peak and the crowd VAT material
     * reads it as a short hue-preserving pulse. It used to decay by `dt * 8.5`
     * inside fixedUpdate — but a hit also calls engine.hitstop(), and during a
     * freeze the engine scales the
     * simulated delta to zero, so the fixed loop takes NO steps at all. The
     * flash therefore sat pinned at 1.0 for the whole 4-12 frame punch, which
     * is exactly the window the player is looking at, and a C4 that catches
     * twenty-five bodies turned the ring into white paper cut-outs for the
     * entire freeze. The freeze is the point of the punch; the white-out is not.
     */
    update(w, _dt, _alpha, realDt) {
      const e = w.entities, k = realDt * HIT_FLASH_DECAY;
      for (let i = 0; i < e.liveN; i++) {
        const id = e.live[i];
        if (e.flash[id] > 0) e.flash[id] = Math.max(0, e.flash[id] - k);
      }
    },

    fixedUpdate(w, dt) {
      const e = w.entities;
      if (!checked && w.anim) {
        checked = true;
        // `src/ai/*` owns non-player bodies whenever it is present; combat only
        // drives reactions itself in a bare harness.
        api.ownsReactions = !w.ai?.hurt;
        const problems = validateMovesets(clipInfoOf(w));
        if (problems.length) console.warn('[combat] moveset drift:\n  ' + problems.join('\n  '));
        else console.log('[combat] movesets ok:', Object.keys(MOVESETS).join(', '),
          '| reactions:', api.ownsReactions ? 'combat' : 'ai');
      }

      // 1. bodies combat owns move first, so the hash below is correct
      if (api.ownsReactions) stepReactions(w, dt);

      // 2. corpses: hold for a beat, then sink and recycle
      for (let i = 0; i < e.liveN; i++) {
        const id = e.live[i];
        if (e.state[id] === ST.DEAD) {
          // The player owns his death/result lifecycle. His stable slot must
          // remain visible and addressable until an explicit battle reset.
          if (id === w.playerId) { deadT[id] = 0; e.fade[id] = 1; continue; }
          deadT[id] += dt;
          if (deadT[id] > 1.9) {
            e.fade[id] -= dt * 0.8;
            if (e.fade[id] <= 0) { rown[id] = 0; w.ai?.discharge?.(id); e.release(id); }
          }
        }
        // Hit flash is decayed in update() on REAL time, not here — see the
        // note there. fixedUpdate does not run at all during hitstop.
        // poise regrows between combos; the AI owns `stagger`, so leave it be
        if (e.maxPoise[id] > 0 && e.poise[id] < e.maxPoise[id] && e.state[id] !== ST.DEAD) {
          e.poise[id] = Math.min(e.maxPoise[id], e.poise[id] + e.maxPoise[id] * 0.34 * dt);
        }
      }

      // 3. rebuild the spatial hash from this tick's positions
      e.refresh();

      // 4. every open hitbox sweeps again — the window stays live for its
      //    whole active span so anyone who walks in still gets hit, while the
      //    per-swing token guarantees nobody is hit twice.
      for (let i = 0; i < WINDOWS; i++) {
        const W_ = wins[i];
        if (!W_.open) continue;
        if (!e.alive[W_.id]) { W_.open = false; continue; }
        resolveWindow(w, W_);
        W_.left -= 1;
        if (W_.left <= 0) W_.open = false;
      }
      if (api.activeArc && !api.activeArc.open) api.activeArc = null;
      api.comboCount = w.player ? w.player.chain : 0;
    },
  };
}
